export interface UserDisplayNameFields {
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
}

/**
 * True when `displayName` is a real label (not blank / classic
 * `` `${undefined} ${undefined}` `` corruption).
 */
export const isUsablePersonDisplayName = (value?: string | null): boolean => {
  if (typeof value !== 'string') {
    return false;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  // Stored bug from composing missing first/last without nullish coalescing.
  if (/^undefined(\s+undefined)*$/i.test(trimmed)) {
    return false;
  }
  return true;
};

const composeFullNameFromParts = (user?: UserDisplayNameFields | null): string =>
  [user?.firstName, user?.lastName]
    .filter(
      (part): part is string =>
        typeof part === 'string' && part.trim().length > 0 && part.trim().toLowerCase() !== 'undefined',
    )
    .map(part => part.trim())
    .join(' ');

/**
 * UI label for a person: prefer first+last, then stored `displayName`.
 * Ignores corrupted `"undefined undefined"` displayName values.
 */
export const resolveUserDisplayName = (
  user?: UserDisplayNameFields | null,
  fallback = '',
): string => {
  const fromParts = composeFullNameFromParts(user);
  if (fromParts) {
    return fromParts;
  }
  const stored = typeof user?.displayName === 'string' ? user.displayName.trim() : '';
  if (isUsablePersonDisplayName(stored)) {
    return stored;
  }
  return fallback;
};
